import { useState, useEffect, useRef } from 'react';
import { Grid, List, Eye } from 'lucide-react';
import PageWrapper from '../PageWrapper/PageWrapper';
import Card from '../../components/Card/Card';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SlideOver from '../../components/SlideOver/SlideOver';
import StoreLinkSection from '../../components/StoreLinkSection/StoreLinkSection';
import DragDropZone from '../../components/DragDropZone/DragDropZone';
import StoreOfferFormSteps from '../../components/StoreOfferFormSteps/StoreOfferFormSteps';
import StoreOfferCard from '../../components/StoreOfferCard/StoreOfferCard';
import IconButton from '../../components/IconButton/IconButton';
import ConfirmationPopup from '../../components/ConfirmationPopup/ConfirmationPopup';
import { Icon } from '../../components/Icon/Icon';
import deleteIcon from '../../assets/images/bin.svg';
import edit from '../../assets/images/pen.svg';
import NoDataCTA from '../../components/NoDataCTA/NoDataCTA';
import { useStoreOffers } from '../../hooks/useStoreOffers';
import { StoreOffer, GownType, GownStyle } from '../../api/StoreOffersAPI';
import ListContainer from '../../components/ListContainer/ListContainer';
import ListElement from '../../components/ListElement/ListElement';
import ElementActions from '../../components/ElementActions/ElementActions';
import CONFIG from '../../config/config';
import { TabV2, TabsList, TabsTrigger, TabsContent } from '../../components/TabV2/TabV2';
import Switch from '../../components/Switch/Switch';
import { LabelV2 } from '../../components/LabelV2/LabelV2';
import PaymentsNotice from '../../components/PaymentsNotice/PaymentsNotice';
import { InputV2 } from '../../components/InputV2/InputV2';
import { TextareaV2 } from '../../components/TextareaV2/TextareaV2';

const EMPTY_OFFER: StoreOffer = {
  name: '',
  description: '',
  price: 0,
  currency: 'EUR',
  images: [],
  featuredImage: '',
  gownType: 'a-line',
  gownStyle: 'classic',
  gownColor: 'white',
  size: '',
  available: true,
  quantity: undefined,
  featured: false,
  onSale: false,
  salePrice: undefined,
  dressFeatures: {
    fitAndFlare: false,
    sweetheart: false,
    lowBack: false,
    strapless: false,
    floorLength: false,
  },
  specialFeatures: [],
  condition: {
    isNew: false,
    hasFlaws: false,
    dryCleaned: false,
    fabricType: '',
    hasBeenAltered: false,
  },
  returnsPolicy: {
    misrepresented: true,
    counterfeit: true,
    doesntFit: false,
    damaged: true,
    neverArrives: true,
    changeOfMind: false,
  },
  labelSize: '',
  year: undefined,
  sellerName: '',
  sellerType: 'private',
  sellerLocation: '',
};

const getGownTypeLabel = (type: GownType): string => {
  const labels: Record<GownType, string> = {
    'ballgown': 'Ballgown',
    'mermaid': 'Mermaid',
    'a-line': 'A-Line',
    'sheath': 'Sheath',
    'trumpet': 'Trumpet',
    'tea-length': 'Tea Length',
    'other': 'Other',
  };
  return labels[type] || type;
};

const getGownStyleLabel = (style: GownStyle): string => {
  const labels: Record<GownStyle, string> = {
    'classic': 'Classic',
    'modern': 'Modern',
    'vintage': 'Vintage',
    'boho': 'Boho',
    'romantic': 'Romantic',
    'minimalist': 'Minimalist',
  };
  return labels[style] || style;
};

const OnlineStorePage = () => {
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<StoreOffer>(EMPTY_OFFER);
  const [isOperationLoading, setIsOperationLoading] = useState<boolean>(false);
  const [customLinkText, setCustomLinkText] = useState<string>('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [stepTitle, setStepTitle] = useState<string>('New Offer');
  const [stepHandlers, setStepHandlers] = useState<{ handleNext: () => void; handlePrevious: () => void; canProceed: () => boolean } | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [termsAndPrivacyContent, setTermsAndPrivacyContent] = useState<string>('');
  const [editorKey, setEditorKey] = useState<number>(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const [localStoreSettings, setLocalStoreSettings] = useState({
    headerImageUrl: '',
    headerText: '',
    showBookAppointmentButton: true,
  });

  const {
    storeOffers,
    isLoading,
    isStoreEnabled,
    isUpdatingEnabled,
    storeSettings,
    isUpdatingSettings,
    createStoreOffer,
    updateStoreOffer,
    deleteStoreOffer,
    setStoreLink,
    reorderStoreOffers,
    updateStoreEnabledStatus,
    updateStoreSettings,
  } = useStoreOffers();

  const handleEdit = (offer: StoreOffer) => {
    setFormData(offer);
    setIsEditing(!!offer.id);
    setCurrentStep(1);
    setEditOpen(true);
  };

  const handleDelete = async (offer: StoreOffer) => {
    setIsOperationLoading(true);
    await deleteStoreOffer(offer.id!);
    setIsOperationLoading(false);
  };

  const handleNewOffer = () => {
    setFormData(EMPTY_OFFER);
    setIsEditing(false);
    setCurrentStep(1);
    setEditOpen(true);
  };

  const handleFilesDropped = (files: File[]) => {
    // Create a new offer with the dropped images
    const imageUrls = files.map(file => URL.createObjectURL(file));
    const newOffer: StoreOffer = {
      ...EMPTY_OFFER,
      images: imageUrls,
      featuredImage: imageUrls[0],
    };
    setFormData(newOffer);
    setIsEditing(false);
    setCurrentStep(1);
    setEditOpen(true);
  };

  const storeLink = `${window.location.origin}/store/${customLinkText || 'demo'}`;

  useEffect(() => {
    setCustomLinkText('');
  }, []);

  useEffect(() => {
    setLocalStoreSettings({
      headerImageUrl: storeSettings.headerImageUrl || '',
      headerText: storeSettings.headerText || '',
      showBookAppointmentButton: storeSettings.showBookAppointmentButton !== false,
    });
  }, [storeSettings]);

  const handleSaveStoreSettings = async () => {
    await updateStoreSettings(localStoreSettings);
  };

  const handleCustomLinkTextChange = (text: string) => {
    setCustomLinkText(text);
    setStoreLink(text);
  };

  const handleSave = async () => {
    setIsOperationLoading(true);
    if (formData.id) {
      await updateStoreOffer(formData.id, formData);
    } else {
      await createStoreOffer(formData);
    }
    setIsOperationLoading(false);
    setEditOpen(false);
    setFormData(EMPTY_OFFER);
    setFormErrors({});
  };

  const handleGenerateInitialTemplate = () => {
    const template = `<h2>Terms and Conditions</h2>
<p>By using our ecommerce platform, you agree to the following terms and conditions:</p>

<h3>1. Product Information</h3>
<p>All product descriptions, images, and prices are provided for informational purposes. We strive to ensure accuracy but cannot guarantee that all information is complete or error-free.</p>

<h3>2. Orders and Payment</h3>
<p>Orders are subject to availability. Payment must be received before items are shipped. We accept various payment methods as indicated during checkout.</p>

<h3>3. Shipping and Delivery</h3>
<p>Shipping times and costs will be calculated at checkout. Delivery times are estimates and may vary based on location and shipping method selected.</p>

<h3>4. Returns and Refunds</h3>
<p>Please review our return policy before making a purchase. Returns must be initiated within the specified timeframe and items must be in original condition.</p>

<h3>5. Privacy</h3>
<p>We respect your privacy and handle your personal information in accordance with our Privacy Policy. Your data will be used solely for order processing and customer service purposes.</p>

<h3>6. Limitation of Liability</h3>
<p>We are not liable for any indirect, incidental, or consequential damages arising from the use of our platform or products.</p>

<p><strong>Last updated:</strong> ${new Date().toLocaleDateString()}</p>`;
    setTermsAndPrivacyContent(template);
    // Force remount by changing key
    setEditorKey(prev => prev + 1);
  };

  useEffect(() => {
    if (editorRef.current) {
      if (termsAndPrivacyContent) {
        // Only set if content is different to avoid unnecessary updates
        if (editorRef.current.innerHTML !== termsAndPrivacyContent) {
          editorRef.current.innerHTML = termsAndPrivacyContent;
        }
      } else if (!editorRef.current.innerHTML || editorRef.current.innerHTML.trim() === '') {
        // Set placeholder text if empty
        editorRef.current.innerHTML = '<p class="text-gray-400">Start typing or click "Generate initial template" to begin...</p>';
      }
    }
  }, [editorKey, termsAndPrivacyContent]);

  return (
    <PageWrapper>
      <>
        <div className="space-y-5">
          <Card>
            <>
              <div className="p-4 space-y-6">
                {/* Enable/Disable Store Switch */}
                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <LabelV2 htmlFor="store-enabled">
                        Enable Ecommerce Site
                      </LabelV2>
                      <p className="text-sm text-gray-600 mt-1">
                        Allow customers to view and purchase items from your public store link
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={isStoreEnabled}
                        onChange={() => updateStoreEnabledStatus(!isStoreEnabled)}
                        disabled={isUpdatingEnabled}
                      />
                      {isUpdatingEnabled && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap">
                  <div className="container flex flex-wrap space-x-5">
                    <PrimaryButton
                      text="Create New Offer"
                      fn={handleNewOffer}
                    />
                  </div>
                  <StoreLinkSection
                    storeLink={storeLink}
                    customLinkText={customLinkText}
                    onCustomLinkTextChange={handleCustomLinkTextChange}
                  />
                </div>
              </div>
            </>
          </Card>

          <Card>
            <>
              <TabV2 defaultValue="offer" className="w-full">
                <TabsList className="grid w-full grid-cols-4 py-[15px]">
                  <TabsTrigger value="offer">
                    Offer
                  </TabsTrigger>
                  <TabsTrigger value="settings">
                    Store Settings
                  </TabsTrigger>
                  <TabsTrigger value="terms-privacy">
                    Terms and Privacy
                  </TabsTrigger>
                  <TabsTrigger value="payments">
                    Payments
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="offer">
                  <div className="mb-6">
                    <DragDropZone
                      onFilesDropped={handleFilesDropped}
                      className="mb-4"
                    />
                  </div>

                  {/* View Switcher */}
                  {storeOffers.length > 0 && (
                    <div className="flex justify-end mb-4">
                      <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => setViewMode('grid')}
                          className={`
                            p-2 rounded transition-all duration-200
                            ${viewMode === 'grid' 
                              ? 'bg-white shadow-sm text-primary-600' 
                              : 'text-gray-600 hover:text-gray-900'
                            }
                          `}
                          title="Grid view"
                        >
                          <Grid className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewMode('list')}
                          className={`
                            p-2 rounded transition-all duration-200
                            ${viewMode === 'list' 
                              ? 'bg-white shadow-sm text-primary-600' 
                              : 'text-gray-600 hover:text-gray-900'
                            }
                          `}
                          title="List view"
                        >
                          <List className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {isLoading ? (
                    viewMode === 'grid' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className="bg-gray-200 animate-pulse rounded-lg h-96" />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className="bg-gray-200 animate-pulse rounded-lg h-24" />
                        ))}
                      </div>
                    )
                  ) : storeOffers.length === 0 ? (
                    <NoDataCTA
                      headline="No Offers Yet"
                      subtext="Create your first offer to get started"
                      primaryActionText="Create New Offer"
                      onPrimaryAction={handleNewOffer}
                    />
                  ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {storeOffers.map((offer) => (
                        <div key={offer.id} className="relative group">
                          <StoreOfferCard
                            offer={offer}
                          />
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2 bg-white rounded-lg shadow-md p-1 z-10">
                            <IconButton onClick={() => handleEdit(offer)}>
                              <Icon url={edit} cssStyles="w-4" />
                            </IconButton>
                            <IconButton>
                              <ConfirmationPopup
                                onConfirm={() => handleDelete(offer)}
                                confirmText="Confirm"
                                title={`Remove: ${offer.name}`}
                              >
                                <Icon url={deleteIcon} cssStyles="w-4" />
                              </ConfirmationPopup>
                            </IconButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ListContainer
                      elements={storeOffers}
                      enableDragAndDrop={true}
                      onMovedItem={(reorderedOffers: StoreOffer[]) => reorderStoreOffers(reorderedOffers)}
                      toElementFn={(offer: StoreOffer) => (
                        <ListElement key={offer.id}>
                          <ElementActions actions={
                            <>
                              <IconButton onClick={() => handleEdit(offer)}>
                                <Icon url={edit} cssStyles="w-4" />
                              </IconButton>
                              <IconButton>
                                <ConfirmationPopup
                                  onConfirm={() => handleDelete(offer)}
                                  confirmText="Confirm"
                                  title={`Remove: ${offer.name}`}
                                >
                                  <Icon url={deleteIcon} cssStyles="w-4" />
                                </ConfirmationPopup>
                              </IconButton>
                            </>
                          }>
                            <div className="flex items-center space-x-3">
                              {/* Thumbnail */}
                              <div className="flex-shrink-0">
                                {offer.featuredImage || offer.images[0] ? (
                                  <img
                                    src={offer.featuredImage || offer.images[0]}
                                    alt={offer.name}
                                    className="w-12 h-12 object-cover rounded-lg"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f3f4f6" width="400" height="400"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                                    }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                    <Eye className="h-6 w-6 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Item Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <p className="font-medium text-sm truncate">
                                    {offer.name}
                                  </p>
                                  {offer.featured && (
                                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                      Featured
                                    </span>
                                  )}
                                  {offer.onSale && (
                                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                      On Sale
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-4 mt-1">
                                  {offer.onSale && offer.salePrice !== undefined ? (
                                    <>
                                      <span className="text-sm font-semibold text-red-600">
                                        {CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[offer.currency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS] || offer.currency}{offer.salePrice.toLocaleString()}
                                      </span>
                                      <span className="text-xs text-gray-400 line-through">
                                        {CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[offer.currency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS] || offer.currency}{offer.price.toLocaleString()}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-sm text-gray-600">
                                      {CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[offer.currency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS] || offer.currency}{offer.price.toLocaleString()}
                                    </span>
                                  )}
                                  {offer.description && (
                                    <span className="text-xs text-gray-500 truncate max-w-xs">
                                      {offer.description}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="text-xs text-gray-500">
                                    {getGownTypeLabel(offer.gownType)} • {getGownStyleLabel(offer.gownStyle)}
                                  </span>
                                  {offer.size && (
                                    <span className="text-xs text-gray-500">
                                      • Size: {offer.size}
                                    </span>
                                  )}
                                  {offer.available && offer.quantity !== undefined && (
                                    <span className="text-xs text-gray-500">
                                      • {offer.quantity > 0 ? `${offer.quantity} in stock` : 'Out of stock'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </ElementActions>
                        </ListElement>
                      )}
                    />
                  )}
                </TabsContent>
                
                <TabsContent value="settings">
                  <div className="py-4 space-y-6">
                    <div>
                      <LabelV2 htmlFor="header-image-url">
                        Header Image URL
                      </LabelV2>
                      <p className="text-sm text-gray-600 mt-1 mb-2">
                        Enter the URL of the image to display at the top of your store page
                      </p>
                      <InputV2
                        id="header-image-url"
                        type="url"
                        placeholder="https://example.com/header-image.jpg"
                        value={localStoreSettings.headerImageUrl}
                        onChange={(e) => setLocalStoreSettings(prev => ({ ...prev, headerImageUrl: e.target.value }))}
                        className="w-full"
                      />
                      {localStoreSettings.headerImageUrl && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-600 mb-2">Preview:</p>
                          <div className="relative h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                            <img
                              src={localStoreSettings.headerImageUrl}
                              alt="Header preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const parent = (e.target as HTMLImageElement).parentElement;
                                if (parent && !parent.querySelector('.error-message')) {
                                  const errorDiv = document.createElement('div');
                                  errorDiv.className = 'error-message flex items-center justify-center h-full text-gray-400';
                                  errorDiv.textContent = 'Image failed to load';
                                  parent.appendChild(errorDiv);
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <LabelV2 htmlFor="header-text">
                        Header Text
                      </LabelV2>
                      <p className="text-sm text-gray-600 mt-1 mb-2">
                        Introductory text displayed over the header image
                      </p>
                      <TextareaV2
                        id="header-text"
                        placeholder="Discover our exquisite collection of wedding dresses"
                        value={localStoreSettings.headerText}
                        onChange={(e) => setLocalStoreSettings(prev => ({ ...prev, headerText: e.target.value }))}
                        className="w-full min-h-[100px]"
                        rows={3}
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <div>
                          <LabelV2 htmlFor="show-book-appointment">
                            Show "Book Appointment" Button
                          </LabelV2>
                          <p className="text-sm text-gray-600 mt-1">
                            Display the "Book Appointment" button next to the "Buy Now" button on product detail pages
                          </p>
                        </div>
                        <Switch
                          checked={localStoreSettings.showBookAppointmentButton}
                          onChange={() => setLocalStoreSettings(prev => ({ ...prev, showBookAppointmentButton: !prev.showBookAppointmentButton }))}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <PrimaryButton
                        text={isUpdatingSettings ? 'Saving...' : 'Save Settings'}
                        fn={handleSaveStoreSettings}
                        isDisabled={isUpdatingSettings}
                        isLoading={isUpdatingSettings}
                      />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="terms-privacy">
                  <div className="py-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <LabelV2 htmlFor="terms-privacy-editor">
                        Terms and Privacy Policy
                      </LabelV2>
                      <PrimaryButton
                        text="Generate initial template"
                        fn={handleGenerateInitialTemplate}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      This is the terms and privacy policy that will be displayed to customers when they purchase products from your store. Make sure to include all relevant information about returns, shipping, privacy, and liability.
                    </p>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <div
                        key={editorKey}
                        ref={editorRef}
                        contentEditable
                        id="terms-privacy-editor"
                        className="min-h-[400px] p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 prose max-w-none"
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                        }}
                        onInput={(e) => {
                          const target = e.currentTarget;
                          setTermsAndPrivacyContent(target.innerHTML);
                        }}
                        suppressContentEditableWarning
                      />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                          onClick={() => {
                            document.execCommand('bold', false);
                            document.getElementById('terms-privacy-editor')?.focus();
                          }}
                          title="Bold"
                        >
                          <strong>B</strong>
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                          onClick={() => {
                            document.execCommand('italic', false);
                            document.getElementById('terms-privacy-editor')?.focus();
                          }}
                          title="Italic"
                        >
                          <em>I</em>
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                          onClick={() => {
                            document.execCommand('underline', false);
                            document.getElementById('terms-privacy-editor')?.focus();
                          }}
                          title="Underline"
                        >
                          <u>U</u>
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                          onClick={() => {
                            document.execCommand('formatBlock', false, '<h2>');
                            document.getElementById('terms-privacy-editor')?.focus();
                          }}
                          title="Heading 2"
                        >
                          H2
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                          onClick={() => {
                            document.execCommand('formatBlock', false, '<h3>');
                            document.getElementById('terms-privacy-editor')?.focus();
                          }}
                          title="Heading 3"
                        >
                          H3
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                          onClick={() => {
                            document.execCommand('formatBlock', false, '<p>');
                            document.getElementById('terms-privacy-editor')?.focus();
                          }}
                          title="Paragraph"
                        >
                          P
                        </button>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="payments">
                  <div className="py-4 space-y-4">
                    <p className="text-sm text-gray-600">
                      We charge a commission on each sale made through your store. The commission rate starts at a maximum of 7% and decreases as your sales volume increases. The more you sell, the lower your commission percentage.
                    </p>
                    <PaymentsNotice />
                  </div>
                </TabsContent>
              </TabV2>
            </>
          </Card>
        </div>
        <SlideOver
          isOpen={editOpen}
          setIsOpen={setEditOpen}
          title={isEditing ? (formData.name || 'Edit Offer') : stepTitle}
          primaryButton={isEditing ? {
            label: 'Save',
            onClick: handleSave,
          } : (currentStep === 6 ? {
            label: 'Save',
            onClick: handleSave,
          } : stepHandlers ? {
            label: 'Next',
            onClick: stepHandlers.handleNext,
          } : undefined)}
          secondaryButton={isEditing ? {
            label: 'Cancel',
            onClick: () => setEditOpen(false),
          } : (currentStep === 1 ? {
            label: 'Cancel',
            onClick: () => setEditOpen(false),
          } : stepHandlers ? {
            label: 'Previous',
            onClick: stepHandlers.handlePrevious,
          } : {
            label: 'Cancel',
            onClick: () => setEditOpen(false),
          })}
          isLoading={isOperationLoading}
          hasErrors={isEditing ? false : (Object.keys(formErrors).length > 0 || (stepHandlers ? !stepHandlers.canProceed() : false))}
        >
        {formData && (
          <StoreOfferFormSteps
            offer={formData}
            onChange={(updatedOffer, errors) => {
              setFormData(updatedOffer);
              setFormErrors(errors);
            }}
            isCreating={!isEditing}
            onStepChange={(step, title, handlers) => {
              setCurrentStep(step);
              setStepTitle(title);
              setStepHandlers(handlers);
            }}
          />
        )}
        </SlideOver>
      </>
    </PageWrapper>
  );
};

export default OnlineStorePage;

